import { ReactNode, useEffect, useRef } from 'react';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { Link } from '../../app/router';
import './auth-ui.css';

export function MemberAuthShell({ title, subtitle, step = 1, children, embedded = false }: { title: ReactNode; subtitle: string; step?: number; children: ReactNode; embedded?: boolean }) {
  const Container = embedded ? 'section' : 'main';
  return <Container className="member-auth"><section className="member-auth__panel">
    <header className="member-auth__nav"><Link to="/" className="icon-button"><ArrowLeft aria-label="กลับหน้าแรก" /></Link><div className="member-auth__progress" aria-label={`ขั้นตอน ${step} จาก 3`}>{[1, 2, 3].map(value => <span key={value} className={value === step ? 'is-active' : ''} />)}</div><span className="member-auth__nav-spacer" /></header>
    <div className="member-auth__heading"><p className="member-auth__step">{step === 1 ? 'เริ่มต้นกับ BEAW' : step === 2 ? 'ยืนยันตัวตน' : 'พร้อมออกไปค้นพบ'}</p><h1>{title}</h1><p>{subtitle}</p></div>
    <div className="member-auth__content">{children}</div>
    <footer className="member-auth__footer"><ShieldCheck size={16} /><span>บัญชีเดียว เข้าใช้ได้ทั้งแอปและเว็บ</span></footer>
  </section></Container>;
}

export function OtpCodeField({ value, onChange, disabled }: { value: string; onChange: (value: string) => void; disabled: boolean }) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const latestValue = useRef(value);
  latestValue.current = value;
  useEffect(() => {
    if (!disabled) inputs.current[Math.min(latestValue.current.length, 5)]?.focus();
  }, [disabled]);
  const focus = (index: number) => inputs.current[Math.max(0, Math.min(index, 5))]?.focus();
  const normalizeDigits = (raw: string) => raw.normalize('NFKC').replace(/[๐-๙]/g, digit => String(digit.charCodeAt(0) - 0x0e50)).replace(/\D/g, '');
  const enter = (index: number, raw: string) => {
    const digits = normalizeDigits(raw).slice(0, 6);
    if (!digits) return;
    const start = digits.length === 6 ? 0 : Math.min(index, value.length);
    const next = (value.slice(0, start) + digits + value.slice(start + digits.length)).slice(0, 6);
    onChange(next);
    focus(start + digits.length);
  };
  return <div className="otp-field" role="group" aria-label="กรอกรหัสยืนยัน 6 หลัก">
    {Array.from({ length: 6 }, (_, index) => <input
      key={index}
      ref={element => { inputs.current[index] = element; }}
      className={value.length === index ? 'is-current' : value[index] ? 'is-filled' : ''}
      required disabled={disabled}
      aria-label={index === 0 ? 'รหัส OTP 6 หลัก' : `รหัส OTP หลักที่ ${index + 1}`}
      type="text" inputMode="numeric" autoComplete={index === 0 ? 'one-time-code' : 'off'}
      pattern="[0-9]" maxLength={6} value={value[index] || ''}
      onFocus={event => event.currentTarget.select()}
      onChange={event => {
        if (!event.target.value) onChange(value.slice(0, index) + value.slice(index + 1));
        else enter(index, event.target.value);
      }}
      onPaste={event => {
        const digits = normalizeDigits(event.clipboardData.getData('text'));
        if (digits) { event.preventDefault(); enter(index, digits); }
      }}
      onKeyDown={event => {
        // Handle physical keyboards directly; mobile input and autofill still use onChange.
        if (event.ctrlKey || event.metaKey || event.altKey || event.nativeEvent.isComposing) return;
        if (event.key.length === 1 && normalizeDigits(event.key)) {
          event.preventDefault();
          enter(index, event.key);
          return;
        }
        if (event.key === 'ArrowLeft') { event.preventDefault(); focus(index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); focus(index + 1); }
        if (event.key === 'Backspace') {
          event.preventDefault();
          const removeAt = value[index] ? index : index - 1;
          if (removeAt >= 0) onChange(value.slice(0, removeAt) + value.slice(removeAt + 1));
          focus(Math.max(0, removeAt));
        }
      }}
    />)}
  </div>;
}
